import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * A vertically scrollable container that caps its height so long lists
 * (e.g. the crew picker inside the schedule-creator dialog) stay reachable
 * instead of pushing the dialog — and its action buttons — off-screen.
 *
 * Mirrors the bounded-scroll pattern already used for the inline assistant
 * menus in CrewSchedule (`max-h-[...] overflow-auto`), but as a single,
 * reusable, testable primitive. Radix `ScrollArea` is intentionally avoided
 * here because it only scrolls reliably with a fixed height, whereas long
 * crew lists need a max-height that adapts to short lists too.
 *
 * `maxHeightClassName` is configurable so the same primitive can back the
 * other bounded lists in the app that cap at different heights.
 */
export interface ScrollableListProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Tailwind max-height utility controlling where scrolling kicks in. */
  maxHeightClassName?: string;
}

export function ScrollableList({
  maxHeightClassName = "max-h-[40vh]",
  className,
  children,
  ...props
}: ScrollableListProps) {
  return (
    <div className={cn(maxHeightClassName, "overflow-y-auto pr-1", className)} {...props}>
      {children}
    </div>
  );
}
